import React, { useState, useContext, useEffect, useRef } from 'react';
import { UserContext } from './UserProvider';
import { userAxios, setupAxiosInterceptors } from '../userAxios';

const API_URL = process.env.REACT_APP_API_URL;
export const ContactContext = React.createContext('');

const ContactProvider = (props) => {
  const { token, handleInvalidToken } = useContext(UserContext);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [callContactForRefresh, setCallContactForRefresh] = useState(false);
  const showContactModalRef = useRef(false);
  const [updatedContactId, setUpdatedContactId] = useState(null);

  useEffect(() => {
    setupAxiosInterceptors(handleInvalidToken);
  }, [handleInvalidToken]);

  const getContacts = async (vendorId) => {
    try {
      const response = await userAxios.get(
        `${API_URL}/vendors/${vendorId}/contacts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setContacts(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return [];
    }
  };

  const getContact = async (contactId) => {
    try {
      const response = await userAxios.get(`${API_URL}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
        'Content-Type': 'application/json',
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching contact:', error);
    }
  };

  const createContact = async (contact) => {
    try {
      const response = await userAxios.post(`${API_URL}/contacts`, contact, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts((prevContacts) => [...prevContacts, response.data]);
    } catch (error) {
      console.error('Error creating contact:', error);
    }
  };

  const updateContact = async (contact) => {
    try {
      const response = await userAxios.put(
        `${API_URL}/contacts/${contact.rowKey}`,
        contact,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setContacts((prevContacts) =>
        prevContacts.map((c) =>
          c.rowKey === contact.rowKey ? response.data : c
        )
      );
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const deleteContact = async (contactId, vendorId) => {
    try {
      await userAxios.delete(`${API_URL}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await getContacts(vendorId);
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const getContactTypes = async () => {
    try {
      const response = await userAxios.get(`${API_URL}/contacts/types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching contact types:', error);
    }
  };

  const createUser = async (user) => {
    try {
      await userAxios.post(`${API_URL}/users`, user, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const createAccount = async (email, vendorId, contactId) => {
    try {
      await userAxios.post(
        `${API_URL}/portal/accounts`,
        {
          email,
          vendor: Number(vendorId),
          contactId: Number(contactId),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      if (error.response?.status === 409) {
        alert('Account Already Activated');
      } else {
        alert('Account failed to activate');
      }
    }
  };

  const disableAccount = async (id, contact) => {
    try {
      await userAxios.post(
        `${API_URL}/portal/accounts/${id}/disable`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      alert('Account failed to deactivate');
    }
  };

  return (
    <ContactContext.Provider
      value={{
        contacts,
        setContacts,
        selectedContact,
        setSelectedContact,
        callContactForRefresh,
        setCallContactForRefresh,
        getContacts,
        getContact,
        createContact,
        updateContact,
        deleteContact,
        createAccount,
        disableAccount,
        getContactTypes,
        createUser,
        showContactModalRef,
        updatedContactId,
        setUpdatedContactId,
      }}
    >
      {props.children}
    </ContactContext.Provider>
  );
};

export default ContactProvider;
